/**
 * Per-host configuration: what this deployment is called, who it talks to, and
 * what it can do.
 *
 * ONE worker serves every host. Cloudflare routes several custom domains to it
 * (me.o-kb.com, cam.console.openside.com, …) and the hostname on the request
 * decides which config applies — in the worker for the API proxy and the login
 * gate, and in the client for branding and labels. Nothing here is fetched at
 * runtime: the set of hosts is known at build time, so it is a table in the
 * repo rather than a service to call and fail to reach.
 *
 * Shared deliberately between the worker and the browser, so it must not touch
 * `window`, `document` or any worker-only global. Callers pass the hostname in.
 */

/** A capability the host either implements or does not. */
export interface HostCapabilities {
  /** POST /api/transcribe. Off hides transcription in the publish flow. */
  transcription: boolean
  /** POST /api/generate-metadata. Off means the user writes their own title. */
  metadataGeneration: boolean
  /** POST /api/notes-assist. Off hides the notes AI button entirely. */
  notesAssistant: boolean
  /**
   * POST /api/projects — upload the SOURCES and the edit instead of a film.
   *
   * Not a feature toggle like the three above; a different shape of publish.
   * On, a publish uploads every clip and every audio segment as its own file
   * plus a manifest describing the timeline, and renders NOTHING on the device
   * — the host finishes the film. Off, publishing renders one film here and
   * uploads that, which is what every host did before this existed.
   *
   * Optional so a host without somewhere to continue the edit is not obliged to
   * invent one. Absent reads as false.
   */
  rawProjectUpload?: boolean
}

/**
 * What the two levels of the publish destination are CALLED here.
 *
 * The shape is fixed at two levels because both known hosts happen to be two
 * levels — OKB is space → feed, console is brand → folder — and a two-level
 * picker is what the publish sheet and Settings render. Only the nouns differ,
 * so they are data. A host needing three levels needs a real UI change, not a
 * config entry, and that is the honest boundary of this abstraction.
 */
export interface DestinationTaxonomy {
  level1: { label: string; plural: string }
  level2: { label: string; plural: string }
  /**
   * Narrows the level-2 candidates the host returns.
   *
   * OKB's /api/feeds returns every feed, and only `notebook` feeds accept a
   * post — a filter that used to be hardcoded in the client. A host whose
   * endpoint already returns only postable destinations leaves this out.
   */
  level2Filter?: { sourceType?: string; activeOnly?: boolean }
}

/**
 * The colour tokens a host may override.
 *
 * A deliberately short list. The app defines ~26 colour tokens, and most of
 * them — the greys, the panel and stage backgrounds, the camera chrome — are
 * the identity of the app *as a tool*, not of the brand it is wearing. Letting
 * a host repaint those is how you get an unreadable recorder, so they are not
 * offered. What is offered is the accent family, the ink that sits on it, and
 * the error colour.
 *
 * `accent` and `accentInk` are required together because they are a PAIR: the
 * ink is what gets drawn on top of the accent (button labels, the record
 * chip), and there is no safe default for it — a host that picks a dark accent
 * and inherits OKB's near-black ink has shipped an invisible label.
 */
export interface BrandPalette {
  /** `--accent`. The one colour a viewer would call "the brand colour". */
  accent: string
  /** `--ink-strong`: text and icons drawn ON `accent`. Check the contrast. */
  accentInk: string
  /**
   * `--ok`: the far end of an accent gradient. Named for what it does here,
   * not for the token: it is never a success signal in this app.
   */
  accentEnd?: string
  /**
   * `--accent-soft` and `--glow-accent`. Both are `accent` at a low alpha, so
   * both are DERIVED from it when omitted — which is the point of leaving them
   * optional. Set them only for a non-hex accent (the derivation needs hex) or
   * when a tint wants to differ in hue from the colour it tints.
   */
  accentSoft?: string
  accentGlow?: string
  /** `--danger` and the muted `--danger-ink` used for error prose. */
  danger?: string
  dangerInk?: string
}

/**
 * Both colour schemes, and both are required.
 *
 * The app honours `prefers-color-scheme` and always will. A host that supplies
 * only a dark palette does not get "dark everywhere" — it gets its dark accent
 * bleeding into light mode against a white background, which is exactly the
 * contrast failure nobody notices until a user reports it. Requiring both makes
 * that a type error instead of a bug.
 */
export interface BrandPalettes {
  dark: BrandPalette
  light: BrandPalette
}

export interface HostBrand {
  /** Shown in the UI and the document title. */
  productName: string
  /** The organisation behind it, for copy like "signs you out of all of …". */
  orgName: string
  /**
   * One line under the product name, on the boot hero and the home screen.
   * Write it as the product's own promise — it is not a place for a tenant's
   * nouns, since a host's destination labels live in `destination`.
   */
  tagline: string
  /** The `<meta name="description">`. Not user-visible in the app. */
  description: string
  /** Single SVG path for the mark, inlined into the shell by the worker. */
  markPath: string
  /** Served from public/brand/<key>/. */
  faviconPath: string
  /**
   * The browser chrome colour per scheme — the strip around the page, not a
   * single pixel the app itself draws. See `palette` for those.
   */
  themeColorDark: string
  themeColorLight: string
  /**
   * Per-host colour overrides, injected into the shell by the worker.
   *
   * Omitted for OKB on purpose: `styles/global.css` *is* OKB's palette, and
   * copying it here would give the same colours two homes to drift between.
   * Absent means "use the stylesheet's own values".
   */
  palette?: BrandPalettes
}

export interface HostStorage {
  /**
   * IndexedDB name. **Never change this for a host that has shipped** — it is
   * where every project, clip and note lives, and renaming it orphans them all.
   * New hosts pick a fresh name; different origins keep them apart anyway.
   */
  databaseName: string
  /** Prefix for localStorage keys, for the same reason. */
  keyPrefix: string
  /**
   * Extension for exported project backups, leading dot included.
   *
   * Same "never change this for a host that has shipped" rule as the two
   * above, for a different reason: users have these files on disk and in cloud
   * drives, and an extension nobody's file picker offers is a backup nobody
   * can restore. Reading is extension-agnostic anyway — this only names what
   * new exports are called and what the drop target advertises.
   */
  backupExtension: string
}

export interface HostConfig {
  /** Stable identifier. Used for asset paths — keep it URL-safe. */
  key: string
  /** Every hostname this config serves, including local dev. */
  hostnames: readonly string[]
  /**
   * The host application's origin: its login page and its API.
   *
   * The worker proxies /api/* here and validates the session against it. The
   * client links to `${webOrigin}${loginPath}` when the session has expired.
   */
  webOrigin: string
  /** Path that starts a login and accepts a `next` query parameter. */
  loginPath: string
  /**
   * This deploy has no upstream host, and is not a tenant of one.
   *
   * Every host above o-cam.app is a white-label of someone's API: the
   * hostname picks the brand, the API and the login, and `webOrigin` is all
   * three. o-cam.app has none of that — where a film can go is installed at
   * runtime with a code — so two behaviours have to change rather than be
   * configured around:
   *
   *  - **Page loads are not gated.** There is no session to check. The gate
   *    exists so a tenant's recorder is not served to strangers; an app whose
   *    projects never leave the device has nobody to turn away.
   *  - **`/api/*` is not proxied to one origin.** The app's own endpoints
   *    (install, connect, the upload proxy) still answer; a host API path has
   *    no upstream to go to and is refused rather than sent somewhere.
   *
   * Absent reads as false, so every existing host keeps the gate and the
   * proxy exactly as they were.
   */
  standalone?: boolean
  brand: HostBrand
  capabilities: HostCapabilities
  destination: DestinationTaxonomy
  storage: HostStorage
}

/**
 * Resolve the config for a hostname.
 *
 * Exact match only, and no fallback to a default host: serving OKB's branding
 * and — far worse — proxying to OKB's API from an unrecognised hostname is a
 * cross-tenant leak waiting to happen. An unknown host is an error the worker
 * turns into a 404.
 */
export function resolveHostConfig(
  hostname: string,
  hosts: readonly HostConfig[],
): HostConfig | null {
  const needle = hostname.toLowerCase()
  for (const host of hosts) {
    if (host.hostnames.some((candidate) => candidate.toLowerCase() === needle)) return host
  }
  return null
}

/**
 * Split a product name for the two-tone brand heading.
 *
 * The heading renders the last word in the accent colour ("OKB **Studio**"),
 * which is a wordmark, not a sentence — so a one-word name puts everything in
 * the accent rather than leaving the coloured span empty.
 */
export function splitProductName(productName: string): { lead: string; tail: string } {
  const name = productName.trim().replace(/\s+/g, ' ')
  const cut = name.lastIndexOf(' ')
  if (cut < 0) return { lead: '', tail: name }
  return { lead: name.slice(0, cut), tail: name.slice(cut + 1) }
}

/** The label a host uses for a destination level, for UI copy. */
export function destinationLabel(
  config: HostConfig,
  level: 'level1' | 'level2',
  form: 'label' | 'plural' = 'label',
): string {
  return config.destination[level][form]
}
